/**
 * HOW MANY OF A LANE'S CARDS ARE BELOW ITS OWN FOLD (UX-round D48).
 * «UX-round Dn» = the 2026-09-17 UX round's own numbering (`.roundtable/2026-09-17-restaurant-ux/decisions.md`), not §11's.
 *
 * The board used to be three page-flow columns with no height cap: on the
 * cook's own 1024×768 wall, on a quiet eight-plate service with the WAITING
 * lane EMPTY, the page ran to 1040px — 272px, about a quarter of the board,
 * under a fold nobody scrolls. Worse than absent: the card at the cut showed
 * its dish name and its modifiers with its bump button below the line, so the
 * board displayed work a cook could read and could not act on.
 *
 * Each lane now scrolls inside its own box. That fixes the page but creates the
 * thing the cook actually feared — "the overflow counter scrolls away with the
 * overflow" — so the lane's heading, which never scrolls, has to say how many
 * plates are out of sight. This function is that count, kept pure so it can be
 * tested: jsdom has no layout, and a rectangle comparison buried in an effect
 * would be guarded by nothing at all.
 *
 * A card counts as hidden when ANY part of it is outside the box — above it
 * after a scroll, or below it before one. The rule is deliberately "whole or
 * not at all": a half-visible card is what caused this, and one that has been
 * scrolled halfway off the top is no more actionable than one below the cut.
 */
export function countCardsOutsideFold(
  /** The scrolling lane body's own top and bottom, in viewport coordinates. */
  box: { top: number; bottom: number },
  /** Each card's top and bottom, in the same coordinates, in lane order. */
  cards: readonly { top: number; bottom: number }[],
): number {
  // A box with no height is a box that has not been laid out yet (jsdom, or
  // the frame before the first measure). Claiming every card is hidden there
  // would put a false "+8 below" in the heading on first paint.
  if (box.bottom - box.top <= 0) return 0;
  /*
   * A pixel of tolerance. Sub-pixel layout means a card that fits exactly can
   * measure 0.4px past the edge, and a lane that permanently reads "1 below"
   * with nothing below it is worse than no counter — it is a counter the cook
   * learns to distrust, which is precisely what the lane count is defended for.
   */
  const slack = 1;
  return cards.filter(
    (card) => card.bottom > box.bottom + slack || card.top < box.top - slack,
  ).length;
}
